import { useForm } from 'react-hook-form'
import { formatDateUTC } from 'src/utils/formatDate'
import TableMui from 'src/components/TableMui'
import { ButtonMui } from 'src/components/ButtonMui'
import * as S from './TableLoss.styles'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'src/utils/store'
import { useDispatch } from 'react-redux'
import { actions } from '../../LossState'
import {
  LossTypes, LossDestination, LossDto, LossApiPayload, DamageBatch, FormData, FormDataItems,
} from '../../LossTypes'
import { useCallback, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'

const lossTypes = [LossTypes.MATURATION, LossTypes.DAMAGE, LossTypes.EXPIRATION_DATE]
const lossDestination = [LossDestination.DONATION, LossDestination.SALE, LossDestination.DISCARD]

export const TableLoss = () => {
  const {
    handleSubmit, setValue, watch, reset, register, setFocus, formState: { errors },
  } = useForm<FormDataItems>()

  const dispatch = useDispatch()
  const history = useHistory()
  const lossState = useSelector((state) => state.loss)
  const { addressList, selectLocation, badge } = lossState

  const filteredList = useMemo(() => addressList?.find((address) => address.id === selectLocation), [addressList, selectLocation])

  const lossTypesOptions = useMemo(() => lossTypes.map((type, index) => ({
    description: type,
    id: index,
  })), [])

  const lossDestinationOptions = useMemo(() => lossDestination.map((type, index) => ({
    description: type,
    id: index,
  })), [])

  const goToAdjustmentFromSpreadsheet = () => {
    history.push('/adjustments/adjustment-in-batch')
  }

  useEffect(() => {
    reset()
  }, [reset, selectLocation])

  const onSubmit = useCallback((data: FormDataItems) => {
    const resolveData: DamageBatch[] = data.items.filter((item) => item.quantity > 0)
      .map((item: FormData) => ({
        ...item,
        type: lossTypes[item.type],
        destination: lossDestination[item.destination],
        address_info: {
          shed: filteredList?.shed,
          street: filteredList?.street,
          shelf: filteredList?.shelf,
          bin: filteredList?.bin,
        },
      }))

    if (!resolveData.length) {
      toast.error('Preencha ao menos um item')
      return
    }

    const payloadData: LossApiPayload = {
      damage_batches: resolveData,
      id_model: filteredList?.idModel,
      product_code: filteredList?.productCode,
      id_location: selectLocation,
    }

    const resetAll = () => {
      reset()
      dispatch(actions.clearState())
    }

    const payload: LossDto = {
      LossAdjustment: { batches: payloadData, badge },
      onSuccess: () => { },
      reset: resetAll,
    }
    dispatch(actions.sendLossAdjustment(payload))
  }, [dispatch, filteredList, reset, badge, selectLocation])

  const resolveTableRows = useCallback(() => filteredList && filteredList?.batches?.map((item, index) => {
    const damageQuantityWatch = watch(`items.${index}.quantity`)
    const damageQuantityInput = (

      <S.CustomTextFieldMui
        id={`items.${index}.quantity`}
        name='quantity'
        type='number'
        autoComplete='off'
        inputProps={{
          ...register(`items.${index}.quantity`,
            {
              valueAsNumber: true,
              max: { value: item.quantity, message: 'Quantidade maior que a disponível' },
              min: { value: 1, message: 'A quantidade deve ser maior que 0' },
              pattern: {
                value: /^[0-9]+$/,
                message: 'Somente números',
              },
            }),
          'data-testid': 'quantity-test',
        }}
        error={!!errors.items?.[index]?.quantity}
        label='loss.form.amount'
        helperText={errors.items?.[index]?.quantity?.message}
        onKeyPress={(event) => event.key === 'Enter' && setFocus(`items.${index}.type`)}
      />
    )

    const damageTypeSelect = (
      <S.CustomSelectMui
        id={`items.${index}.types`}
        defaultValue=''
        autoComplete='off'
        inputProps={{ ...register(`items.${index}.type`, { required: !!damageQuantityWatch && 'Campo obrigatório' }), 'data-testid': 'damage-type-test' }}
        error={!!damageQuantityWatch && !!errors.items?.[index]?.type}
        label='Tipo do dano'
        helperText={damageQuantityWatch ? errors.items?.[index]?.type?.message : ''}
        variant='outlined'
        options={lossTypesOptions}
        size='small'
        type='text'
        disabled={!damageQuantityWatch}
        fullWidth
      />
    )

    const damageJustificationInput = (
      <S.CustomTextFieldMui
        id={`items.${index}.reason`}
        name='reason'
        autoComplete='off'
        inputProps={{
          ...register(`items.${index}.reason`,
            { required: !!damageQuantityWatch && 'Campo obrigatório' }),
          'data-testid': 'reason-test',
        }}
        error={!!damageQuantityWatch && !!errors.items?.[index]?.reason}
        label='loss.form.justification'
        helperText={!!damageQuantityWatch && errors.items?.[index]?.reason?.message}
        disabled={!damageQuantityWatch}
        onKeyPress={(event) => event.key === 'Enter' && setFocus(`items.${index}.destination`)}
        fullWidth
      />
    )

    const destinationSelect = (
      <S.CustomSelectMui
        defaultValue=''
        autoComplete='off'
        inputProps={{ ...register(`items.${index}.destination`, { required: !!damageQuantityWatch && 'Campo obrigatório' }), 'data-testid': 'destinationSelect-test' }}
        id={`items.${index}.destination`}
        error={!!damageQuantityWatch && !!errors.items?.[index]?.destination}
        label='Destino'
        helperText={!!damageQuantityWatch && errors.items?.[index]?.destination?.message ? errors.items?.[index]?.destination?.message : ''}
        variant='outlined'
        options={lossDestinationOptions}
        size='small'
        type='text'
        disabled={!damageQuantityWatch}
        fullWidth
      />

    )

    setValue(`items.${index}.expiration_date`, formatDateUTC(item.expirationDate, 'YYYY-MM-DD'))
    setValue(`items.${index}.id_batch`, item.idBatch)
    setValue(`items.${index}.id_stock`, item.idStock)
    setValue(`items.${index}.batch_stock_id`, item.batchStockId)

    return {
      batch: item.idBatch,
      expirationDate: formatDateUTC(item.expirationDate, 'DD/MM/YYYY'),
      totalQuantity: item.quantity,
      damageQuantity: damageQuantityInput,
      damageType: damageTypeSelect,
      damageJustification: damageJustificationInput,
      destination: destinationSelect,
    }
  }), [filteredList, register, setValue, watch, errors, lossTypesOptions, lossDestinationOptions, setFocus])

  return (
    <S.Wrapper onSubmit={handleSubmit(onSubmit)}>
      <TableMui
        columns={[
          { id: 'batch', label: 'Lote', width: '128px' },
          { id: 'expirationDate', label: 'Validade', width: '128px' },
          { id: 'totalQuantity', label: 'Qtd. Total', width: '128px' },
          { id: 'damageQuantity', label: 'Qtd. Avariada', width: '128px' },
          { id: 'damageType', label: 'Tipo', width: '256px' },
          { id: 'damageJustification', label: 'Justificativa' },
          { id: 'destination', label: 'Destino', width: '128px' },
        ]}
        emptyLabel='Nenhum lote encontrado/selecionado'
        rows={resolveTableRows()}
      />
      <S.ButtonsContainer>
        <ButtonMui
          title='Registrar Avarias'
          type='submit'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          testId='register-loss-button'
          disabled={!filteredList}
        />
        <ButtonMui
          title='Importar Avarias'
          type='button'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          onClick={goToAdjustmentFromSpreadsheet}
        />
      </S.ButtonsContainer>
    </S.Wrapper>
  )
}
